<template>
  <nav class="navbar" :class="{ 'navbar-scrolled': isScrolled }">
    <div class="container">
      <div class="logo" @click="navigateTo('/')">
        <img src="@/assets/logo.png" alt="Logo" class="logo-img" />
      </div>

      <!-- 桌面导航菜单 -->
      <ul class="nav-menu" :class="{ 'menu-hidden': isMobileMenuOpen }">
        <li v-for="item in menuItems" :key="item.id">
          <a @click="navigateTo(item.route)" :class="{ active: item.active }">
            {{ t(item.labelKey) }}
          </a>
        </li>
      </ul>

      <!-- 移动端汉堡菜单按钮 -->
      <button
        class="mobile-menu-btn"
        @click="toggleMobileMenu"
        :class="{ active: isMobileMenuOpen }"
        aria-label="Toggle mobile menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <!-- 移动端下拉菜单 -->
      <div class="mobile-menu" :class="{ 'menu-open': isMobileMenuOpen }">
        <ul class="mobile-nav-menu">
          <li v-for="item in menuItems" :key="item.id">
            <a @click="navigateToMobile(item.route)" :class="{ active: item.active }">
              {{ t(item.labelKey) }}
            </a>
          </li>
        </ul>
      </div>

      <div class="nav-actions">
        <div class="lang-switcher" @click="toggleLangDropdown">
          <img :src="currentLangData.flag" :alt="currentLangData.code" class="flag-icon" />
          <span class="lang-text">{{ currentLangData.code }}</span>
          <svg class="dropdown-icon" :class="{ 'rotate': showLangDropdown }" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>

          <div v-if="showLangDropdown" class="lang-dropdown">
            <div
              v-for="lang in languages"
              :key="lang.code"
              class="lang-option"
              :class="{ active: locale === lang.locale }"
              @click.stop="selectLanguage(lang.locale)"
            >
              <img :src="lang.flag" :alt="lang.code" class="flag-icon" />
              <span>{{ lang.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

interface MenuItem {
  id: number
  labelKey: string
  route: string
  active?: boolean
}

interface Language {
  code: string
  name: string
  flag: string
  locale: string
}

const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n()

const isScrolled = ref(false)
const showLangDropdown = ref(false)
const isMobileMenuOpen = ref(false)

const languages = ref<Language[]>([
  { code: 'EN', name: 'English', flag: 'https://flagcdn.com/w40/us.png', locale: 'en' },
  { code: '简中', name: '简体中文', flag: 'https://flagcdn.com/w40/cn.png', locale: 'zh-CN' },
  { code: '繁中', name: '繁體中文', flag: 'https://flagcdn.com/w40/tw.png', locale: 'zh-TW' }
])

const currentLangData = computed(() => {
  return languages.value.find(lang => lang.locale === locale.value) || languages.value[0]
})

const menuItems = ref<MenuItem[]>([
  { id: 1, labelKey: 'nav.technology', route: '/technology' },
  { id: 2, labelKey: 'nav.products', route: '/products' },
  { id: 3, labelKey: 'nav.about', route: '/about' },
  { id: 4, labelKey: 'nav.contact', route: '/contact' }
  // { id: 5, labelKey: 'nav.quality', route: '/quality' },
  // { id: 6, labelKey: 'nav.sales', route: '/sales' },
  // { id: 7, labelKey: 'nav.customers', route: '/customers' }
])

const navigateTo = (routePath: string) => {
  router.push(routePath)
}

const navigateToMobile = (routePath: string) => {
  router.push(routePath)
  isMobileMenuOpen.value = false
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  // 关闭语言选择器，如果它处于打开状态
  if (showLangDropdown.value) {
    showLangDropdown.value = false
  }
}

const toggleLangDropdown = () => {
  showLangDropdown.value = !showLangDropdown.value
  // 关闭移动菜单，如果它处于打开状态
  if (isMobileMenuOpen.value) {
    isMobileMenuOpen.value = false
  }
}

const selectLanguage = (langLocale: string) => {
  console.log('🌍 Language switch initiated:', {
    from: locale.value,
    to: langLocale,
    timestamp: new Date().toISOString()
  })

  // 更新 i18n locale
  locale.value = langLocale
  localStorage.setItem('locale', langLocale)

  console.log('✅ i18n locale updated to:', locale.value)
  console.log('💾 localStorage updated to:', localStorage.getItem('locale'))

  // 触发页面重新渲染，确保语言切换生效
  document.documentElement.lang = langLocale

  showLangDropdown.value = false

  // 显示切换成功的反馈
  console.log('🎉 Language successfully switched to:', langLocale)
}

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.lang-switcher')) {
    showLangDropdown.value = false
  }
  // 点击外部时关闭移动菜单
  if (!target.closest('.mobile-menu-btn') && !target.closest('.mobile-menu')) {
    isMobileMenuOpen.value = false
  }
}

// ESC键关闭移动菜单
const handleEscKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isMobileMenuOpen.value) {
    isMobileMenuOpen.value = false
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleEscKey)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleEscKey)
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bitter:wght@300;400;500;600;700&family=Hind:wght@300;400;500;600;700&display=swap');

.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: transparent;
  backdrop-filter: none;
  transition: all 0.3s ease;
  box-shadow: none;
}

.navbar-scrolled {
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 75px;
  position: relative;
}

.logo-img {
  height: 55px;
  width: auto;
  transition: transform 0.3s ease;
  object-fit: contain;
  margin-top: 5px;
}

.logo-img:hover {
  transform: scale(1.05);
}

.nav-menu {
  display: flex;
  list-style: none;
  gap: 40px;
  margin: 0;
  padding: 0;
}

.nav-menu.menu-hidden {
  display: none;
}

.nav-menu li a {
  text-decoration: none;
  color: #2c3e50;
  font-size: 15px;
  font-weight: 500;
  font-family: 'Hind', sans-serif;
  letter-spacing: 0.3px;
  transition: color 0.3s;
  position: relative;
  padding: 8px 0;
}

.nav-menu li a:hover {
  color: #2196F3;
}

.nav-menu li a.active {
  color: #2196F3;
  font-weight: 600;
  font-family: 'Bitter', serif;
}

.nav-menu li a.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #2196F3 0%, #0D47A1 100%);
  border-radius: 2px;
}

/* 移动端汉堡菜单按钮 */
.mobile-menu-btn {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  z-index: 1001;
}

.mobile-menu-btn span {
  width: 22px;
  height: 2px;
  background-color: #2c3e50;
  margin: 2px 0;
  transition: all 0.3s ease;
  transform-origin: center;
}

.mobile-menu-btn.active span:nth-child(1) {
  transform: rotate(45deg) translate(5px, 5px);
}

.mobile-menu-btn.active span:nth-child(2) {
  opacity: 0;
  transform: scale(0);
}

.mobile-menu-btn.active span:nth-child(3) {
  transform: rotate(-45deg) translate(5px, -5px);
}

/* 移动端下拉菜单 */
.mobile-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-height: 0;
  overflow: hidden;
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateY(-10px);
}

.mobile-menu.menu-open {
  max-height: 400px;
  opacity: 1;
  transform: translateY(0);
}

.mobile-nav-menu {
  list-style: none;
  margin: 0;
  padding: 20px 0;
}

.mobile-nav-menu li {
  text-align: center;
}

.mobile-nav-menu li a {
  display: block;
  padding: 15px 20px;
  text-decoration: none;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 500;
  font-family: 'Hind', sans-serif;
  transition: all 0.3s ease;
  border-bottom: 1px solid #f0f0f0;
}

.mobile-nav-menu li a:hover {
  background-color: #f8f9fa;
  color: #2196F3;
}

.mobile-nav-menu li a.active {
  background-color: #e3f2fd;
  color: #2196F3;
  font-weight: 600;
  font-family: 'Bitter', serif;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.lang-switcher {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #f8f9fa;
  border: 1.5px solid #e0e0e0;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
}

.lang-switcher:hover {
  background-color: #ffffff;
  border-color: #2196F3;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.15);
}

.flag-icon {
  width: 22px;
  height: 16px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.lang-text {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  font-family: 'Bitter', serif;
  letter-spacing: 0.5px;
}

.dropdown-icon {
  transition: transform 0.3s ease;
  color: #666;
}

.dropdown-icon.rotate {
  transform: rotate(180deg);
}

.lang-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  min-width: 180px;
  animation: slideDown 0.3s ease;
  border: 1px solid #e0e0e0;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.lang-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
  font-family: 'Hind', sans-serif;
}

.lang-option:hover {
  background-color: #f0f7ff;
}

.lang-option.active {
  background-color: #e3f2fd;
  color: #2196F3;
  font-weight: 600;
}

.lang-option:not(:last-child) {
  border-bottom: 1px solid #f0f0f0;
}

/* 平板设备 */
@media (max-width: 768px) {
  .container {
    padding: 0 20px;
    height: 65px;
  }

  .nav-menu {
    gap: 25px;
  }

  .nav-menu li a {
    font-size: 14px;
  }

  .logo-img {
    height: 48px;
    margin-top: 3px;
  }

  .lang-switcher {
    padding: 6px 12px;
  }
}

/* 手机设备 */
@media (max-width: 480px) {
  .nav-menu {
    display: none;
  }

  .mobile-menu-btn {
    display: flex;
  }

  .container {
    justify-content: space-between;
    padding: 0 15px;
  }

  .logo-img {
    height: 45px;
  }

  .lang-switcher {
    padding: 6px 10px;
    gap: 6px;
  }

  .flag-icon {
    width: 18px;
    height: 14px;
  }

  .lang-text {
    display: none;
  }

  .mobile-nav-menu li a {
    font-size: 15px;
    padding: 12px 20px;
  }
}

/* 小型手机设备 */
@media (max-width: 360px) {
  .mobile-nav-menu li a {
    font-size: 14px;
    padding: 10px 15px;
  }
}
</style>